import { useState, useEffect, useCallback } from "react";
import api, { resolveImageUrl } from "../lib/api";
import { BarChart3, TrendingUp, Gem, Target } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";

const chartStyle = { fontSize: 11, fill: "#71717a" };
const tooltipStyle = { backgroundColor: "#18181b", border: "1px solid #27272a", borderRadius: "8px" };
const tooltipLabelStyle = { color: "#a1a1aa" };
const lineDot = { fill: "#e11d48", r: 3 };
const xAxisDomain = [0, 100];
const barRadiusH = [0, 4, 4, 0];
const barRadiusV = [4, 4, 0, 0];

function ValueTimeline({ timeline }) {
  if (!timeline?.length) return null;
  return (
    <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-5">
      <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-4">Collection Value Over Time</h2>
      <div className="h-64 min-h-[16rem]">
        <ResponsiveContainer width="100%" height="100%" minWidth={1}>
          <LineChart data={timeline}>
            <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={chartStyle} />
            <YAxis tick={chartStyle} tickFormatter={(v) => `$${v}`} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`$${v}`, "Value"]} labelStyle={tooltipLabelStyle} />
            <Line type="monotone" dataKey="value" stroke="#e11d48" strokeWidth={2} dot={lineDot} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CompletionChart({ title, data: chartData, dataKey, layout, fill, barRadius }) {
  if (!chartData?.length) return <p className="text-zinc-500 text-sm">No data yet</p>;
  const isVertical = layout === "vertical";
  return (
    <div className="h-64 min-h-[16rem]">
      <ResponsiveContainer width="100%" height="100%" minWidth={1}>
        <BarChart data={chartData} layout={layout}>
          <CartesianGrid stroke="#27272a" horizontal={!isVertical} vertical={isVertical ? false : undefined} />
          {isVertical ? (
            <>
              <XAxis type="number" tick={chartStyle} domain={xAxisDomain} tickFormatter={(v) => `${v}%`} />
              <YAxis type="category" dataKey={dataKey} tick={chartStyle} width={100} />
            </>
          ) : (
            <>
              <XAxis dataKey={dataKey} tick={chartStyle} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={chartStyle} domain={xAxisDomain} tickFormatter={(v) => `${v}%`} />
            </>
          )}
          <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v}%`, "Completion"]} labelStyle={tooltipLabelStyle} />
          <Bar dataKey="percentage" fill={fill} radius={barRadius} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function MostValuableList({ items }) {
  if (!items?.length) return null;
  return (
    <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-5">
      <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-4 flex items-center gap-2">
        <Gem className="w-4 h-4 text-amber-400" /> Most Valuable Models
      </h2>
      <div className="space-y-2">
        {items.map((r, i) => (
          <div key={r.release_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800/50 transition-colors">
            <span className="text-xs font-mono text-zinc-500 w-6">#{i + 1}</span>
            {r.model_image ? (
              <img src={resolveImageUrl(r.model_image)} alt="" className="w-10 h-10 rounded-lg object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center text-[8px] text-zinc-600 font-mono">{r.khmg_number}</div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-zinc-200 truncate">{r.release_name}</p>
              <p className="text-[11px] text-zinc-500">{r.vehicle}</p>
            </div>
            <span className="font-mono text-sm text-emerald-400 font-semibold">{r.estimated_market_value != null ? `$${r.estimated_market_value}` : 'TBD'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const { data } = await api.get("/analytics");
      setData(data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [setData, setLoading]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {["analytics-skel-1", "analytics-skel-2", "analytics-skel-3"].map((id) => <div key={id} className="h-64 bg-zinc-900 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div data-testid="analytics-page" className="p-4 sm:p-6 lg:p-8 space-y-8 animate-fadeIn">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-zinc-100 tracking-tight flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-rose-500" /> Analytics
        </h1>
        <p className="text-zinc-500 mt-1 text-sm">Collection insights and statistics</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-4">
          <TrendingUp className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="font-mono text-2xl font-bold text-emerald-400">${data.total_collection_value?.toLocaleString()}</p>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Collection Value</p>
        </div>
        <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-4">
          <Gem className="w-5 h-5 text-amber-400 mb-2" />
          <p className="font-mono text-2xl font-bold text-amber-400">{data.chase_stats?.owned}/{data.chase_stats?.total}</p>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Chase Variants</p>
        </div>
        <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-4">
          <Target className="w-5 h-5 text-blue-400 mb-2" />
          <p className="font-mono text-2xl font-bold text-blue-400">${data.total_spent?.toLocaleString()}</p>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Total Spent</p>
        </div>
        <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-4">
          <TrendingUp className="w-5 h-5 text-rose-400 mb-2" />
          <p className="font-mono text-2xl font-bold text-rose-400">${(data.total_collection_value - data.total_spent)?.toLocaleString()}</p>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Net Profit</p>
        </div>
      </div>

      {/* Collection Value Over Time */}
      <ValueTimeline timeline={data.value_timeline} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Series Completion */}
        <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-5">
          <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-4">Completion by Series</h2>
          <CompletionChart data={data.series_completion} dataKey="series" layout="vertical" fill="#e11d48" barRadius={barRadiusH} />
        </div>

        {/* Vehicle Completion */}
        <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-5">
          <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-4">Completion by Vehicle</h2>
          <CompletionChart data={data.vehicle_completion} dataKey="vehicle" fill="#10b981" barRadius={barRadiusV} />
        </div>
      </div>

      {/* Most Valuable */}
      <MostValuableList items={data.most_valuable} />
    </div>
  );
}
